import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Eye, RefreshCw, Globe, Trash2, Calendar, Users, Bot, Server, TrendingUp,
  MapPin, FileText, Link2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X,
} from 'lucide-react';
import { BACKEND_URL as API } from '../../lib/api';

const authHead = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_console_token')}` });
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const C = {
  ink: '#0F172A', slate: '#475569', muted: '#94A3B8', border: '#E5E7EB', soft: '#F1F5F9',
  bar: '#94A3B8', humans: '#10B981', total: '#1E293B', link: '#64748B',
};
const TONES = {
  slate: { bg: '#F8FAFC', bd: '#E2E8F0', fg: '#64748B' },
  green: { bg: '#ECFDF5', bd: '#A7F3D0', fg: '#059669' },
  amber: { bg: '#FFFBEB', bd: '#FDE68A', fg: '#D97706' },
  blue:  { bg: '#EFF6FF', bd: '#BFDBFE', fg: '#2563EB' },
  red:   { bg: '#FEF2F2', bd: '#FECACA', fg: '#DC2626' },
};
const RANGES = [[1, 'Today'], [7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [0, 'All time'], ['custom', 'Custom']];
const SITE = 'keystone';

const sinceFor = (days) => (days ? new Date(Date.now() - days * 864e5).toISOString() : '');
const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};
const refLabel = (r) => {
  if (!r) return '';
  const m = r.match(/^https?:\/\/([^/]+)/i);
  return (m ? m[1] : r).replace(/^www\./, '').toLowerCase();
};

function Flag({ code }) {
  if (!code || code.length !== 2) return <span style={{ display: 'inline-block', width: 20 }} />;
  return <img src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`} alt={code} width="20" height="14"
    style={{ borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />;
}

function Card({ icon: Icon, title, right, children, testId, style }) {
  return (
    <section data-testid={testId} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '16px 14px', ...style }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.slate }}>
            {Icon && <Icon size={13} strokeWidth={1.8} />}{title}
          </div>
          {right && <div style={{ fontSize: 11.5, color: C.slate }}>{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

function ActionBtn({ icon: Icon, label, onClick, danger, testId, busy }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={busy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 6, fontSize: 13,
      background: danger ? '#FEF2F2' : '#fff', color: danger ? '#DC2626' : C.ink,
      border: `1px solid ${danger ? '#FCA5A5' : C.border}`, cursor: busy ? 'wait' : 'pointer',
      transition: 'background-color .15s',
    }}><Icon size={14} strokeWidth={1.8} className={busy ? 'vi-spin' : ''} />{label}</button>
  );
}

function RangeCard({ range, setRange, custom, setCustom }) {
  return (
    <Card icon={Calendar} title="Date range" testId="vi-date-range">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {RANGES.map(([v, l]) => {
          const on = range === v;
          return (
            <button key={l} data-testid={`vi-range-${v}`} onClick={() => setRange(v)} style={{
              padding: '6px 10px', fontSize: 12, borderRadius: 5, cursor: 'pointer',
              background: on ? C.total : '#fff', color: on ? '#fff' : C.ink,
              border: `1px solid ${on ? C.total : C.border}`, transition: 'background-color .15s',
            }}>{l}</button>
          );
        })}
        {range === 'custom' && ['from', 'to'].map((k) => (
          <input key={k} type="date" data-testid={`vi-custom-${k}`} value={custom[k]}
            onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value }))}
            style={{ padding: '5px 8px', fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 5, color: C.ink }} />
        ))}
      </div>
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
  const t = TONES[tone];
  return (
    <div data-testid={testId} style={{ background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 6, padding: '14px 12px', minHeight: 70 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.fg }}>
        <Icon size={13} strokeWidth={1.8} style={{ flexShrink: 0 }} />{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: C.ink, marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function TrendBars({ series }) {
  if (!series?.length) return <div style={{ height: 90, display: 'grid', placeItems: 'center', color: C.muted, fontSize: 12 }}>No data in this window yet.</div>;
  const max = Math.max(1, ...series.map((s) => s.total));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110 }}>
        {series.map((s) => (
          <div key={s.date} title={`${s.date}: ${s.total} total, ${s.humans} humans`} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
            <div style={{ width: '62%', maxWidth: 34, height: `${(s.total / max) * 100}%`, background: C.total, borderRadius: '3px 3px 0 0', position: 'relative', minHeight: s.total ? 2 : 0 }}>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${s.total ? (s.humans / s.total) * 100 : 0}%`, background: C.humans, borderRadius: s.humans === s.total ? '3px 3px 0 0' : 0 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {series.map((s, i) => (
          <div key={s.date} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
            {series.length <= 16 || i % Math.ceil(series.length / 12) === 0 ? s.date.slice(5) : ''}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 11, color: C.slate }}>
        {[['Humans', C.humans], ['Total', C.total]].map(([l, c]) => (
          <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l}
          </span>
        ))}
      </div>
    </>
  );
}

function TopList({ icon, title, rows, total, mono, flags, action, onAction, testId, labelStyle }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <Card icon={icon} title={title} testId={testId}>
      {!rows?.length ? <div style={{ fontSize: 12, color: C.muted }}>Nothing yet.</div> : rows.map((r, i) => (
        <div key={`${r.label}-${i}`} data-testid={`${testId}-row-${i}`} style={{ padding: '4px 0 3px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            {flags && <Flag code={r.country_code} />}
            <span title={r.label} style={{
              color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
              fontFamily: mono ? MONO : 'inherit', fontSize: mono ? 11.5 : 12.5, ...(labelStyle?.(r) || {}),
            }}>{r.display || r.label}</span>
            {action && r.actionable !== false && (
              <button data-testid={`${testId}-${action}-${i}`} onClick={() => onAction(r)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5,
                color: C.link, textDecoration: 'underline', textUnderlineOffset: 2, fontFamily: mono ? MONO : 'inherit', flexShrink: 0,
              }}>{action}</button>
            )}
            <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', color: C.ink, flexShrink: 0 }}>{r.count}</span>
            <span style={{ width: 46, textAlign: 'right', fontSize: 10.5, color: C.muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {total ? ((r.count / total) * 100).toFixed(1) : '0.0'}%
            </span>
          </div>
          <div style={{ height: 3, background: C.soft, borderRadius: 2, marginTop: 4, marginRight: 54 }}>
            <div style={{ height: '100%', width: `${(r.count / max) * 100}%`, background: C.bar, borderRadius: 2, transition: 'width .4s ease' }} />
          </div>
        </div>
      ))}
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.slate }}>{label}</span>
      {children}
    </label>
  );
}
const inputStyle = { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12.5, color: C.ink, background: '#fff', width: '100%', boxSizing: 'border-box', outline: 'none' };

function LogFilters({ f, set }) {
  return (
    <Card testId="vi-log-filters" style={{ marginBottom: 14 }}>
      <div className="vi-filters" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 10 }}>
        <Field label="Site">
          <select data-testid="vi-f-site" style={inputStyle} value="all" onChange={() => {}}>
            <option value="all">All sites</option><option value={SITE}>{SITE}</option>
          </select>
        </Field>
        <Field label="Audience">
          <select data-testid="vi-f-audience" style={inputStyle} value={f.bot} onChange={(e) => set('bot', e.target.value)}>
            <option value="">Humans or bots</option><option value="false">Humans</option><option value="true">Bots</option>
          </select>
        </Field>
        <Field label="Country (ISO-2)">
          <input data-testid="vi-f-country" style={inputStyle} maxLength={2} value={f.country} onChange={(e) => set('country', e.target.value.toUpperCase())} />
        </Field>
        <Field label="Path contains">
          <input data-testid="vi-f-path" style={inputStyle} value={f.path} onChange={(e) => set('path', e.target.value)} />
        </Field>
        <Field label="IP starts with">
          <input data-testid="vi-f-ip" style={inputStyle} value={f.ip} onChange={(e) => set('ip', e.target.value)} />
        </Field>
        <Field label="Page size">
          <select data-testid="vi-f-size" style={inputStyle} value={f.size} onChange={(e) => set('size', parseInt(e.target.value, 10))}>
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} rows</option>)}
          </select>
        </Field>
      </div>
    </Card>
  );
}

function RefCell({ r }) {
  const label = refLabel(r.referrer);
  if (!label) return <span style={{ fontStyle: 'italic', color: C.muted }}>Direct</span>;
  if (/preview\.emergentagent|localhost/.test(label)) return <span style={{ fontStyle: 'italic', color: C.slate }}>Site development</span>;
  return <a href={r.referrer} target="_blank" rel="noreferrer" style={{ color: C.ink, textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{label}</a>;
}

function LogTable({ data, setPage }) {
  const th = { textAlign: 'left', padding: '14px 10px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.slate, whiteSpace: 'nowrap' };
  const td = { padding: '10px', fontSize: 12, color: C.ink, borderTop: `1px solid ${C.soft}`, whiteSpace: 'nowrap' };
  return (
    <section data-testid="vi-log-table" style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['When', 'Site', 'Path', 'IP'].map((h) => <th key={h} style={th}>{h}</th>)}
            <th style={th}><span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><Link2 size={11} />Referrer</span></th>
          </tr></thead>
          <tbody>
            {(data.items || []).map((r, i) => (
              <tr key={r.visit_id || i} data-testid={`vi-log-row-${i}`}>
                <td style={{ ...td, fontSize: 11, color: C.slate, fontVariantNumeric: 'tabular-nums' }}>{fmtWhen(r.received_at)}</td>
                <td style={td}>{SITE}</td>
                <td style={{ ...td, fontFamily: MONO, fontSize: 11.5, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.path}>{r.path || '/'}</td>
                <td style={{ ...td, fontFamily: MONO, fontSize: 11.5 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Flag code={r.country_code} />{r.ip}</span>
                </td>
                <td style={{ ...td, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}><RefCell r={r} /></td>
              </tr>
            ))}
            {!data.items?.length && <tr><td colSpan="5" style={{ ...td, textAlign: 'center', color: C.muted, padding: 40 }}>No visits match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 14px', borderTop: `1px solid ${C.soft}`, fontSize: 12, color: C.slate }}>
        <span data-testid="vi-log-pageinfo">Showing page {data.page} of {Math.max(1, data.pages)} · {data.total.toLocaleString()} total</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[[ChevronsLeft, 1, data.page <= 1, 'first'], [ChevronLeft, data.page - 1, data.page <= 1, 'prev'],
            [ChevronRight, data.page + 1, data.page >= data.pages, 'next'], [ChevronsRight, data.pages, data.page >= data.pages, 'last']]
            .map(([Icon, p, dis, k]) => (
              <button key={k} data-testid={`vi-pg-${k}`} disabled={dis} onClick={() => setPage(p)} style={{
                width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 6, border: `1px solid ${C.border}`,
                background: '#fff', color: C.slate, opacity: dis ? 0.4 : 1, cursor: dis ? 'not-allowed' : 'pointer',
              }}><Icon size={14} /></button>
            ))}
        </div>
      </div>
    </section>
  );
}

function useWindow(range, custom) {
  if (range === 'custom') return { since: custom.from || '', until: custom.to || '' };
  return { since: sinceFor(range), until: '' };
}

export default function VisitorInsights({ onUnauth }) {
  const [range, setRange] = useState(7);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [ov, setOv] = useState(null);
  const [logs, setLogs] = useState(null);
  const [f, setF] = useState({ bot: '', country: '', path: '', ip: '', size: 50 });
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const logsRef = useRef(null);
  const win = useWindow(range, custom);

  const fail = (e) => { if (e?.response?.status === 401) onUnauth(); else setMsg(e?.message || 'Failed to load'); };

  useEffect(() => {
    let off = false;
    const days = range === 'custom' ? 0 : range;
    const params = new URLSearchParams({ days, since: win.since, until: win.until });
    axios.get(`${API}/api/admin/overview?${params}`, { headers: authHead() })
      .then((r) => { if (!off) setOv(r.data); }).catch(fail);
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win.since.slice(0, 13), win.until, tick]);

  useEffect(() => {
    let off = false;
    const params = new URLSearchParams({ page, page_size: f.size, bot: f.bot, country: f.country, path: f.path, ip_prefix: f.ip, since: win.since, until: win.until });
    axios.get(`${API}/api/marketing/ip-logs?${params}`, { headers: authHead() })
      .then((r) => { if (!off) setLogs(r.data); }).catch(fail);
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, f, win.since.slice(0, 13), win.until, tick]);

  const setFilter = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const drill = (k, v) => { setFilter(k, v); logsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const backfill = async () => {
    setBusy('geo'); setMsg('');
    try {
      await axios.post(`${API}/api/marketing/ip-logs/backfill?max_rows=5000`, null, { headers: authHead() });
      setMsg('Geo backfill started. Locations fill in over the next few minutes.');
    } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const rotate = async () => {
    if (!window.confirm('Delete every visitor log older than 12 months?')) return;
    setBusy('rotate'); setMsg('');
    try {
      const r = await axios.post(`${API}/api/admin/visitor-logs/rotate`, null, { headers: authHead() });
      setMsg(`Rotated logs: ${r.data.deleted} entries older than 12 months removed.`); setTick((t) => t + 1);
    } catch (e) { fail(e); } finally { setBusy(''); }
  };

  const h = ov?.headline;
  const total = h?.total || 0;
  const max = Math.max(0, ...(ov?.time_series || []).map((s) => s.total));
  const refs = Object.values((ov?.top_referrers || []).reduce((acc, r) => {
    const dev = /preview\.emergentagent|localhost/.test(r.label);
    const direct = r.label === '(direct / no referrer)';
    const key = dev ? '__dev' : r.label;
    if (acc[key]) acc[key].count += r.count;
    else acc[key] = { ...r, display: direct ? 'Direct' : dev ? 'Site development' : r.label, actionable: !direct && !dev };
    return acc;
  }, {})).sort((a, b) => b.count - a.count);
  const italic = (r) => (r.actionable === false ? { fontStyle: 'italic', color: C.slate } : null);
  const activeChips = [['bot', f.bot && (f.bot === 'true' ? 'Bots' : 'Humans')], ['country', f.country], ['path', f.path], ['ip', f.ip]].filter(([, v]) => v);

  return (
    <div data-testid="admin-iplogs" style={{ maxWidth: 1080, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 26, fontWeight: 600, color: C.ink, margin: 0, letterSpacing: '-0.01em' }}>
        <Eye size={22} strokeWidth={2} />Visitor Insights
      </h1>
      <p style={{ fontSize: 13.5, color: C.slate, margin: '6px 0 14px' }}>Who's hitting the sites, from where, how often. Auto-purge after 12 months.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <ActionBtn icon={RefreshCw} label="Refresh" testId="vi-refresh" onClick={() => setTick((t) => t + 1)} />
        <ActionBtn icon={Globe} label={busy === 'geo' ? 'Starting…' : 'Backfill geo'} testId="vi-backfill" busy={busy === 'geo'} onClick={backfill} />
        <ActionBtn icon={Trash2} label="Rotate logs" danger testId="vi-rotate" busy={busy === 'rotate'} onClick={rotate} />
      </div>
      {msg && <div data-testid="vi-msg" style={{ fontSize: 12.5, color: C.slate, background: C.soft, borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>{msg}</div>}

      <RangeCard range={range} setRange={(v) => { setRange(v); setPage(1); }} custom={custom} setCustom={setCustom} />

      <div className="vi-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10, margin: '18px 0' }}>
        <Kpi icon={Eye} label="Visits" value={h?.humans} tone="slate" testId="vi-kpi-visits" />
        <Kpi icon={Users} label="Humans" value={h?.humans} tone="green" testId="vi-kpi-humans" />
        <Kpi icon={Bot} label="Bots / Crawlers" value={h?.bots} tone="amber" testId="vi-kpi-bots" />
        <Kpi icon={Server} label="Unique IPs" value={h?.unique_ips} tone="blue" testId="vi-kpi-ips" />
        <Kpi icon={Globe} label="Countries" value={h?.unique_countries} tone="red" testId="vi-kpi-countries" />
      </div>

      <Card icon={TrendingUp} title="Daily trend" right={`${ov?.time_series?.length || 0} days · max ${max}`} testId="vi-trend">
        <TrendBars series={ov?.time_series} />
      </Card>

      <div className="vi-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
        <TopList icon={Server} title="Top IPs" testId="vi-top-ips" rows={ov?.top_ips} total={h?.humans} mono flags action="filter" onAction={(r) => drill('ip', r.label)} />
        <TopList icon={Globe} title="Top Countries" testId="vi-top-countries" rows={(ov?.top_countries || []).map((r) => ({ ...r, country_code: r.label }))} total={total} flags action="filter" onAction={(r) => drill('country', r.label)} />
        <TopList icon={MapPin} title="Top Cities" testId="vi-top-cities" rows={ov?.top_cities} total={total} flags />
        <TopList icon={Server} title="Top ISPs" testId="vi-top-isps" rows={ov?.top_isps} total={total} />
        <TopList icon={FileText} title="Top Pages" testId="vi-top-pages" rows={(ov?.top_pages || []).slice(0, 10)} total={total} mono action="filter" onAction={(r) => drill('path', r.label)} />
        <TopList icon={Link2} title="Top Referrers" testId="vi-top-referrers" rows={refs.slice(0, 10)} total={refs.reduce((a, r) => a + r.count, 0)} action="visit" labelStyle={italic}
          onAction={(r) => window.open(`https://${r.label}`, '_blank', 'noopener')} />
      </div>

      <div ref={logsRef} style={{ marginTop: 30, scrollMarginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: C.ink, margin: 0 }}>Visitor Logs</h2>
          {activeChips.map(([k, v]) => (
            <button key={k} data-testid={`vi-chip-${k}`} onClick={() => setFilter(k, '')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '3px 8px', borderRadius: 999,
              background: C.soft, border: `1px solid ${C.border}`, color: C.slate, cursor: 'pointer',
            }}>{v}<X size={11} /></button>
          ))}
        </div>
        <LogFilters f={f} set={setFilter} />
        {logs ? <LogTable data={logs} setPage={setPage} /> : <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>Loading…</div>}
      </div>

      <style>{`
        .vi-spin { animation: vi-spin 1s linear infinite; }
        @keyframes vi-spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) { .vi-kpis { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } .vi-grid { grid-template-columns: 1fr !important; } .vi-filters { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
      `}</style>
    </div>
  );
}
